import { useEffect, useState } from 'react';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';

type ExportableItems = {
    images: Image3D[];
    structureSets: StructureSet[];
    doses: DoseMatrix[];
    registrations: RigidRegistration[];
};

const useExportSelections = (
    exportableStructureSets: StructureSet[] = [],
    exportableDoses: DoseMatrix[] = [],
    modalOpen?: boolean,
    exportableRegistrations: RigidRegistration[] = []
) => {
    const [selectedImages, setSelectedImages] = useState<Image3D[]>([]);
    const [selectedStructureSets, setSelectedStructureSets] = useState<
        StructureSet[]
    >([]);
    const [selectedDoses, setSelectedDoses] = useState<DoseMatrix[]>([]);
    const [selectedRegistrations, setSelectedRegistrations] = useState<
        RigidRegistration[]
    >([]);
    const [structureSelections, setStructureSelections] = useState<
        Record<string, DicomRoi[]>
    >({});

    useEffect(() => {
        const newStructureSets = exportableStructureSets.filter(
            (ss) => ss.isNew
        );
        const newDoses = exportableDoses.filter((dose) => dose.isNew);
        const newRegistrations = exportableRegistrations.filter(
            (reg) => reg.isNew
        );
        if (newStructureSets?.length > 0) {
            setSelectedStructureSets(newStructureSets);
            newStructureSets.forEach((structureSet) => {
                initializeStructureSelections(structureSet);
            });
        }
        if (newDoses?.length > 0) {
            setSelectedDoses(newDoses);
        }
        if (newRegistrations.length > 0) {
            setSelectedRegistrations(newRegistrations);
        }
        // Exhaustive deps are ignored here as other deps will cause an infinite loop
        // because exportableStructureSets and exportableDoses are recreated on each render, but they
        // also cannot be memoized as underlying objects are not immutable so changes to them wouldn't
        // be detected correctly, causing parts of UI not to update as expected.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen]);

    const initializeStructureSelections = (structureSet: StructureSet) => {
        const approvedStructures = structureSet.roiArray.filter(
            (structure) => structure.isApproved
        );
        setStructureSelections((prev) => ({
            ...prev,
            [structureSet.dicomUid]: approvedStructures.length
                ? approvedStructures
                : structureSet.roiArray.filter(
                      (structure) =>
                          structure.approvalStatus !==
                          DICOMApprovalStatus.REJECTED
                  ),
        }));
    };

    const handleSelectImage = (image: Image3D) => {
        setSelectedImages((prev) =>
            prev.includes(image)
                ? prev.filter((id) => id !== image)
                : [...prev, image]
        );
    };

    const handleSelectStructureSet = (structureSet: StructureSet) => {
        setSelectedStructureSets((prev) =>
            prev.includes(structureSet)
                ? prev.filter((id) => id !== structureSet)
                : [...prev, structureSet]
        );

        if (!structureSelections[structureSet.dicomUid]) {
            initializeStructureSelections(structureSet);
        }
    };

    const handleSelectStructure = (
        structureSetId: string,
        structure: DicomRoi
    ) => {
        setStructureSelections((prev) => {
            const currentSelections = prev[structureSetId] || [];
            const updatedSelections = currentSelections.includes(structure)
                ? currentSelections.filter((s) => s !== structure)
                : [...currentSelections, structure];

            return {
                ...prev,
                [structureSetId]: updatedSelections,
            };
        });
    };

    const handleSelectDose = (dose: DoseMatrix) => {
        setSelectedDoses((prev) =>
            prev.includes(dose)
                ? prev.filter((id) => id !== dose)
                : [...prev, dose]
        );
    };

    const handleSelectRegistration = (registration: RigidRegistration) => {
        setSelectedRegistrations((prev) =>
            prev.includes(registration)
                ? prev.filter((r) => r !== registration)
                : [...prev, registration]
        );
    };

    const handleSelectAll = (
        exportableItems: ExportableItems,
        selectAll: boolean
    ): void => {
        if (selectAll) {
            setSelectedImages(exportableItems.images);
            setSelectedStructureSets(exportableItems.structureSets);
            setSelectedDoses(exportableItems.doses);
            setSelectedRegistrations(exportableItems.registrations);
            exportableItems.structureSets.forEach((ss) => {
                if (!structureSelections[ss.dicomUid]) {
                    initializeStructureSelections(ss);
                }
            });
        } else {
            setSelectedImages([]);
            setSelectedStructureSets([]);
            setSelectedDoses([]);
            setSelectedRegistrations([]);
        }
    };

    const resetSelections = () => {
        setSelectedImages([]);
        setSelectedStructureSets([]);
        setSelectedDoses([]);
        setSelectedRegistrations([]);
        setStructureSelections({});
    };

    return {
        selectedImages,
        selectedStructureSets,
        selectedDoses,
        selectedRegistrations,
        structureSelections,
        handleSelectImage,
        handleSelectStructureSet,
        handleSelectStructure,
        handleSelectDose,
        handleSelectRegistration,
        handleSelectAll,
        resetSelections,
    };
};

export default useExportSelections;
